// src/components/ImpersonationBanner.tsx
// A persistent, always-visible indicator shown while an operator is in an
// impersonated ("support") session (flag set by ImpersonationEntry). Exiting
// clears the session tokens and returns to login. Self-contained: reads
// localStorage, no router/redux needed, so it can mount above everything.
import React, { useEffect, useState } from 'react';
import { purgeLegacyAuth } from '@/lib/session';

export const ImpersonationBanner: React.FC = () => {
  const [who, setWho] = useState<string | null>(() => localStorage.getItem('kp_impersonation'));

  useEffect(() => {
    if (!who) return;
    // Push page content down so the fixed bar doesn't hide the top of the app.
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = '40px';
    const onStorage = () => setWho(localStorage.getItem('kp_impersonation'));
    window.addEventListener('storage', onStorage);
    return () => {
      document.body.style.paddingTop = prev;
      window.removeEventListener('storage', onStorage);
    };
  }, [who]);

  if (!who) return null;

  const exit = () => {
    localStorage.removeItem('kp_impersonation');
    // Purge any legacy localStorage credentials. The httpOnly session cookie is
    // cleared server-side by the login page's sign-out; a full reload lands there.
    purgeLegacyAuth();
    // Full reload clears in-memory/persisted auth and lands on login.
    window.location.href = '/login';
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 40,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        background: 'linear-gradient(90deg, #B45309, #D97706)',
        color: '#fff',
        fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
      }}
    >
      <span>
        ⚠ Support session — you are signed in as <strong>{who}</strong>. Actions are recorded.
      </span>
      <button
        type="button"
        onClick={exit}
        style={{
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.5)',
          color: '#fff',
          borderRadius: 8,
          padding: '3px 12px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Exit support session
      </button>
    </div>
  );
};

export default ImpersonationBanner;
